import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Input, Space, Tag, Tooltip, Typography, message } from 'antd';
import { LockOutlined, TagsOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import {
    aiAdminApi,
    SharedPromptLibraryEntry,
    SubsystemId,
} from '../../../services/ai-admin-api';

const { Text } = Typography;

interface Props {
    /** Owning subsystem id — used to filter the placeholder picker (Layer 2). */
    consumerSubsystem: SubsystemId;
    value: string;
    onChange: (v: string) => void;
    label?: string;
    rows?: number;
    placeholder?: string;
    disabled?: boolean;
}

/**
 * Textarea with a "Insert placeholder" picker that lists only the shared
 * library entries this subsystem is allow-listed for. Layer 2 of the
 * confidentiality model — confidential placeholders never appear in the
 * picker for non-allow-listed subsystems.
 */
export const PromptEditor: React.FC<Props> = ({
    consumerSubsystem,
    value,
    onChange,
    label,
    rows = 8,
    placeholder,
    disabled,
}) => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [available, setAvailable] = useState<SharedPromptLibraryEntry[]>([]);
    const [textareaRef, setTextareaRef] = useState<HTMLTextAreaElement | null>(null);

    const load = useCallback(async () => {
        if (!jwt) return;
        try {
            setAvailable(
                await aiAdminApi.sharedPromptLibraryAvailableFor(jwt, consumerSubsystem),
            );
        } catch (err: any) {
            message.error(err.message || 'Failed to load placeholder picker entries');
        }
    }, [jwt, consumerSubsystem]);

    useEffect(() => {
        load();
    }, [load]);

    const insertAtCursor = (token: string) => {
        if (!textareaRef) {
            onChange(value + token);
            return;
        }
        const start = textareaRef.selectionStart ?? value.length;
        const end = textareaRef.selectionEnd ?? value.length;
        const next = `${value.slice(0, start)}${token}${value.slice(end)}`;
        onChange(next);
        // Restore caret position after React re-renders.
        requestAnimationFrame(() => {
            const pos = start + token.length;
            textareaRef.setSelectionRange(pos, pos);
            textareaRef.focus();
        });
    };

    const menuItems = useMemo(
        () =>
            available.map((entry) => ({
                key: entry.settingKey,
                label: (
                    <Space>
                        <code>{`{${entry.settingKey}}`}</code>
                        {entry.isConfidential && (
                            <Tooltip title={`Confidential — only ${entry.allowedSubsystems.join(', ')} may reference this.`}>
                                <Tag color="gold" icon={<LockOutlined />}>
                                    confidential
                                </Tag>
                            </Tooltip>
                        )}
                        {entry.displayName && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {entry.displayName}
                            </Text>
                        )}
                    </Space>
                ),
                onClick: () => insertAtCursor(`{${entry.settingKey}}`),
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [available, value, textareaRef],
    );

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 4 }}>
                {label && <Text strong>{label}</Text>}
                <Dropdown
                    menu={{ items: menuItems }}
                    placement="bottomRight"
                    disabled={disabled || menuItems.length === 0}
                    trigger={['click']}
                >
                    <Button size="small" icon={<TagsOutlined />} disabled={disabled}>
                        Insert placeholder
                    </Button>
                </Dropdown>
            </Space>
            <Input.TextArea
                ref={(el) => setTextareaRef(el?.resizableTextArea?.textArea ?? null)}
                rows={rows}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                style={{ fontFamily: 'monospace' }}
            />
        </div>
    );
};
